import React, { useEffect, useState } from "react";
import { Select, MenuItem, IconButton, Box, Container, Drawer } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import { get_auth_user_data, get_auth_user_tokens } from "../../redux/auth/auth.selectors";
import { CustomDataGrid } from "../../common/components/Table/CustomDataGrid";
import { useNavigate } from "react-router";
import getErrorMessage, { logout_if_403 } from "../../util";
import axiosInstance from "../../http-common";
import AddUserDrawerComponent from "./AddUserDrawerComponent";
import ContainedButton from "../../common/components/Buttons/ContainedButton";
import { AddOutlined } from "@mui/icons-material";
import RolesListSelectorDataGrid from "./RolesListSelectorDataGrid";

const UserManagementDashboard = () => {
  const [users, setUsers] = useState([]);

  const current_user = useSelector(get_auth_user_data);
  const token_data = useSelector(get_auth_user_tokens);
  const current_user_id = current_user._id;

  const navigate = useNavigate();

  useEffect(() => {
    if (current_user?.role && current_user?.role !== "admin") navigate("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_user]);

  // console.log(token_data);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get(`/v1/user/all?application=qc`, {
        headers: {
          Authorization: token_data.auth_token,
        },
      });
      setUsers(response.data.map(user => ({ ...user, id: user._id.$oid.toString() })));
    } catch (error) {
      logout_if_403(error);
      console.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (current_user?.role && current_user?.role === "admin") {
      fetchUsers();
    }
    else {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeRole = async (id, role) => {
    try {
      await axiosInstance.patch(`/v1/user/role/${id}`, { role }, {
        headers: {
          Authorization: token_data?.auth_token
        }
      });
      setUsers(prevUsers => prevUsers.map(user => user.id === id ? { ...user, role } : user));
    } catch (error) {
      logout_if_403(error);
      console.error(getErrorMessage(error));
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await axiosInstance.delete(`/v1/user/?id=${id}`,
        {
          headers: {
            Authorization: token_data?.auth_token
          }
        }
      );
      setUsers(prevUsers => prevUsers.filter(user => user.id !== id));
    } catch (error) {
      logout_if_403(error);
      console.log(getErrorMessage(error));
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 1, hide: true },
    {
      field: 'name', headerName: 'Name', flex: 1, renderCell: (params) => {
        const { name, last_name } = params?.row;
        return `${name} ${last_name}`;
      }
    },
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'roles_list',
      headerName: 'Roles',
      flex: 1,
      renderCell: (params) => {
        // console.log({ params });
        return <RolesListSelectorDataGrid params={params} setUsers={setUsers} users={users} />;
      },
    },
    {
      field: 'delete',
      headerName: 'Delete User',
      // flex: 1,
      renderCell: (params) => (
        <IconButton
          color="error"
          onClick={() => handleDeleteUser(params.row.id)}
          disabled={params.row.id === current_user_id} // Disable for current user
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const [new_user_added, set_new_user_added] = useState(false);

  useEffect(() => {
    if (new_user_added) {
      fetchUsers()?.finally(set_new_user_added(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [new_user_added]);

  const [add_user_drawer_state, set_add_user_drawer_state] = useState({
    right: false,
  });

  const toggle_add_drawer = (anchor, open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    set_add_user_drawer_state({ ...add_user_drawer_state, [anchor]: open });
  };

  return (
    <div style={{ height: "100%", width: '100%' }}>
      <Drawer
        anchor={"right"}
        open={add_user_drawer_state.right}
        onClose={toggle_add_drawer("right", false)}
        PaperProps={{
          sx: {
            backgroundColor: "transparent !important"
          }
        }}
      >
        <AddUserDrawerComponent
          set_new_user_added={set_new_user_added}
        />
      </Drawer>
      <div className="flex w-full justify-between items-center mb-1">
        <div
          className="p-8"
          style={{
            width: "100%",
            color: "black",
            fontSize: "28px",
            fontFamily: "Poppins",
            fontWeight: 400,
            lineHeight: "36px",
            wordWrap: "break-word",
          }}
        >
          QC Scheduler - Admin Panel
          <br />
        </div>
        <div className="w-max pr-6">
          <ContainedButton sx={{
            width: "max-content"
          }} startIcon={<AddOutlined />} onClick={toggle_add_drawer("right", true)} >Add User</ContainedButton>
        </div>
      </div>
      <Box>
        <Container className="p-2">
          <CustomDataGrid rows={users} columns={columns} pageSize={5} columnVisibilityModel={{
            // Hide columns status and traderName, the other columns will remain visible
            id: false,
          }}
          />
        </Container>
      </Box>
    </div>
  );
};

export default UserManagementDashboard;
